
import { StyledDocument } from "../document/Document.styled";

//react hooks
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";


// zod
import { zodResolver } from "@hookform/resolvers/zod";


// models

import { Document, DocumentEdit, DocumentEditSchema } from "../../../../models/document";
import { DocumentItemEdit } from "../../../../models/document-item";
import { DocumentWorkEdit } from "../../../../models/document-work";
import { DocumentSettings } from "../../../../models/document-settings";
import { DocumentStructure } from "../../../../models/document-structure";
import { DocumentPaymentForm } from "../../../../models/document-payment";
import { DocumentClosingForm } from "../../../../models/document-closing";
import { DocumentDepositAdd } from "../../../../models/document-deposit";

// services
import { saveDocument as saveDocumentService } from "../../api/documents.service";
import { calcKabala, calcWorksAndItems } from "../../utils/documents";

// mui
import { Button } from "@mui/material";


// toast
import { toast } from 'react-toastify';
import { handleErrorWithBoundary, logError, normalizeError } from "@shared/lib/errors";

// components

import {
    DocumentAccountSection as DocumentAccount,
    DocumentBankSection as DocumentBank,
    DocumentCarSection as DocumentCar,
    DocumentDepositSection as Deposit,
    DocumentHeaderSection as Header,
    DocumentInvoicesSection as Invoices,
    DocumentItemsSection as DocumentItems,
    DocumentPaymentsSection as Payments,
    DocumentSummarySection as Summary,
    DocumentTitleSection as Title,
    DocumentWorksSection as DocumentWorks,
} from "../../components/DocumentFormSections";

import { useParams } from 'react-router-dom';
import { useGetDocumentsSettingsQuery } from "../../api/documentsSettings.api";
import { useGetDocumentsStructureQuery } from "@features/structures/api/documentsStructure.api";
import { useGetCashBoxesAccountsQuery, useGetIncomesAccountsQuery } from "@features/tickets/public";
import { Section } from '@shared/ui/layout/Section';
import { useGetDocumentQuery } from "../../api/documents.api";


// import { DevTool } from '@hookform/devtools';
import { ClosingFormSchema, ClosingFormType, DepositsFormSchema, DepostitsFormType, ItemsFormSchema, ItemsFormType, PaymentsFormSchema, PaymentsFormType, WorksFormSchema } from "./EditDocument.types";
import { WorksFormType } from "../add-document/AddDocument.types";
import { getDocumentFormData, getDocumentSettings, getDocumentStructure, getFormItemsRows, getFormWorksRows, getIfDocumentClosed, getPaymentsRows, updateCustomDataArray } from "./EditDocument.utils";
import { UserData } from "../../../../models/user-data";
import Loading from "@app/pages/loading/Loading";
import { useErrorBoundary } from "react-error-boundary";





function EditDocument() {


    // ###################################################################################### //
    // #                                 variables                                          # //
    // ###################################################################################### //


    const method = "edit";
    const { showBoundary } = useErrorBoundary();

    const { id } = useParams();

    const navigate = useNavigate();
    const { state } = useLocation() as { state: UserData; pathname: string }; // state data from another page

    const { data: document }: { data?: Document }  = useGetDocumentQuery(Number(id));
    const { data: documentsSettings } = useGetDocumentsSettingsQuery();
    const { data: documentsStructure } = useGetDocumentsStructureQuery();
    const { data: incomes } = useGetIncomesAccountsQuery(); // all incomes accounts
    const { data: kupaAccounts } = useGetCashBoxesAccountsQuery(); // all kupa accounts

    const settings: DocumentSettings | undefined = useMemo(()=> getDocumentSettings(document), [document]);
    const isDocumentClosed: boolean | undefined = useMemo(()=> getIfDocumentClosed(document), [document]);
    const structure: DocumentStructure[] | undefined = useMemo(()=> getDocumentStructure(documentsStructure, settings), [documentsStructure, settings]);

    const [isSelectDepositsDialogOpen, setIsSelectDepositsDialogOpen] = useState<boolean>(false); // set if open select deposit dialog
    const [isSelectCashDialogOpen, setIsSelectCashDialogOpen] = useState<boolean>(false); // set if open select deposit dialog
    const [documentsToClose, setDocumentsToClose] = useState<Document[]>(); // invoices to close for receipt
    const [isSaving, setIsSaving] = useState<boolean>(false); // set if this doc is saving data right now


    
    // ###################################################################################### //
    // #                                   forms                                            # //
    // ###################################################################################### //



    const documentForm = useForm<DocumentEdit>({
        resolver: zodResolver(DocumentEditSchema),
        defaultValues: getDocumentFormData(state, document)
    });


    const worksForm = useForm<WorksFormType>({
        resolver: zodResolver(WorksFormSchema),
        defaultValues: {
            works: getFormWorksRows(state, document)
        }
    });

    const itemsForm = useForm<ItemsFormType>({
        resolver: zodResolver(ItemsFormSchema),
        defaultValues: {
            items: getFormItemsRows(state, document)
        }
    });

    const paymentsForm = useForm<PaymentsFormType>({
        resolver: zodResolver(PaymentsFormSchema),
        defaultValues: {
            payments: getPaymentsRows(state, document)
        }
    });

    const documentClosingForm = useForm<ClosingFormType>({
        resolver: zodResolver(ClosingFormSchema),
        defaultValues: {
            documents: document?.closing ?? []
        }
    });


    const documentDepositsForm = useForm<DepostitsFormType>({
        resolver: zodResolver(DepositsFormSchema),
        defaultValues: {
            deposits: document?.deposits ?? []
        }
    });


    // ###################################################################################### //
    // #                              forms arrays                                          # //
    // ###################################################################################### //


    const itemsFieldArray = useFieldArray({
        name: 'items',
        control: itemsForm.control
    });

    const worksFieldArray = useFieldArray({
        name: 'works',
        control: worksForm.control
    });

    const paymentsFieldArray = useFieldArray({
        name: 'payments',
        control: paymentsForm.control
    });

    const documentsClosingFieldArray = useFieldArray({
        name: 'documents',
        control: documentClosingForm.control
    });

    const depositsFieldArray = useFieldArray({
        name: 'deposits',
        control: documentDepositsForm.control
    });


    // ###################################################################################### //
    // #                               use effects                                          # //
    // ###################################################################################### //



    useEffect(()=>{
        
        if(document && !state){
            resetFormsWithDocumentData(document);
        }

    }, [document]);




    // *********************************************************************************************


    // calc document.
    useEffect(() => {
        if (settings && incomes && !isDocumentClosed) {
            calcDocument();
        }
    }, [settings, incomes]);




    // *********************************************************************************************


    // calc total payment of documents closing
    useEffect(() => {

        const subscription = documentForm.watch((value, { name }) => {

            if (name === "receipt") {
                calcDocumentsClosing(Number(value[name]));
            }
        });

        return () => subscription.unsubscribe();

    }, [documentForm.watch("receipt")]);



    // ###################################################################################### //
    // #                                functions                                           # //
    // ###################################################################################### //


    function resetFormsWithDocumentData(document: Document){

        documentForm.reset({...document})

        if ( document.works && document.works.length > 0) {
            worksForm.reset({ works: document.works });
        }

        if (document.items && document.items.length > 0) {
            itemsForm.reset({ items: document.items });
        }

        if (document.payments && document.payments.length > 0) {
            paymentsForm.reset({ payments: document.payments });
        }

        if (document.closing && document.closing.length > 0) {
            documentClosingForm.reset({ documents: document.closing });
        }

        if (document.deposits && document.deposits.length > 0) {
            documentDepositsForm.reset({ deposits: document.deposits });
        }
    }




    // *********************************************************************************************


    
    /**
     *  update the document calculation section 
     */
    function calcDocument(): void {
        
        const works = worksForm.getValues().works;
        const items = itemsForm.getValues().items;
        const payments = paymentsForm.getValues().payments;

        if(settings){
            calcWorksAndItems(documentForm, works, items, settings, incomes);
            calcKabala(documentForm, payments, settings, documentsToClose);
        }
        
    }



    // *********************************************************************************************


    function calcDocumentsClosing(receipt: number): void {

        let balance = receipt;

        const updateForm = documentClosingForm.getValues("documents").map(documentClosing => {

            if (documentClosing.old_balance <= balance) {

                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: 0,
                    status: 'סגור',
                    amount: documentClosing.balance,
                };

                balance = balance - documentClosing.balance;

                return documentClosingToRetrun;
            }

            else if (documentClosing.old_balance > balance && balance !== 0) {

                const updated_balance = documentClosing.old_balance - balance
                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: updated_balance,
                    status: 'סגור חלקית',
                    amount: balance,
                };

                balance = 0;

                return documentClosingToRetrun;
            }

            else if (balance === 0) {

                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: documentClosing.balance,
                    status: 'פתוח',
                    amount: balance,
                };

                return documentClosingToRetrun
            }
        });

        documentClosingForm.reset({ documents: updateForm });

    }


    // *********************************************************************************************


    //add a new document with all the forms data
    const saveDocument = async () => {

        if ((settings?.display_works || settings?.display_items) && documentForm.getValues("to_pay") === 0) {
            alert("הסכום לתשלום לא יכול להיות 0.");
            return;
        }

        if ((settings?.display_works || settings?.display_items) && settings?.display_payments && documentForm.getValues("balance") !== 0) {
            alert("ההפרש צריך להיות 0");
            return;
        }

        console.log("addDocument worksForm.getValues() ", worksForm.getValues());
        console.log("addDocument itemsForm.getValues() ", itemsForm.getValues());
        console.log("addDocument paymentsForm.getValues() ", paymentsForm.getValues());
        console.log("addDocument documentForm.getValues() ", documentForm.getValues());

        // documentForm.setValue("document_number", documentNumber!);
        // documentForm.setValue("document_settings_id", settings!.id);

        documentForm.setValue("account_id", documentForm.getValues("document_account.account_id"));
        documentForm.setValue("status", settings?.type === "מסמך רגיל" || settings?.type === "כרטיס עבודה" || settings?.type === "חשבונית מס" ? "פתוח" : "סגור");

        const updatedcustomerCustomData = documentForm.getValues("document_account.custom_data");
        const updatedcarCustomData = documentForm.getValues("car.custom_data");
        const updatedWorksCustomData = worksForm.getValues("works");
        const updatedItemsCustomData = itemsForm.getValues("items");

        updateCustomDataArray(updatedcustomerCustomData);
        updateCustomDataArray(updatedcarCustomData);

        updatedWorksCustomData.forEach((work) => {
            updateCustomDataArray(work.custom_data);
        });


        updatedItemsCustomData.forEach((item) => {
            updateCustomDataArray(item.custom_data);
        });

        documentForm.setValue("document_account.custom_data", updatedcustomerCustomData);
        documentForm.setValue("car.custom_data", updatedcarCustomData);
        worksForm.setValue("works", updatedWorksCustomData);
        itemsForm.setValue("items", updatedItemsCustomData);

        if (!settings?.display_car || !documentForm.getValues("car.account_number")) {
            documentForm.setValue("car", undefined);
        }



        try {

            //add works to documentForm
            if (settings?.display_works) {

                await new Promise<void>((resolve, reject) => {
                    worksForm.handleSubmit((data: { works: DocumentWorkEdit[] }) => {
                        const works = data.works.filter((work) => !(work.total === 0 && work.name === "" && work.work_code === ""));
                        documentForm.setValue("works", works);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }


            //add items to documentForm
            if (settings?.display_items) {

                await new Promise<void>((resolve, reject) => {
                    itemsForm.handleSubmit((data: { items: DocumentItemEdit[] }) => {
                        const items = data.items.filter((item) => !(item.total === 0 && item.name === "" && item.sku === ""));
                        documentForm.setValue("items", items);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add payments to documentForm
            if (settings?.display_payments) {

                await new Promise<void>((resolve, reject) => {
                    paymentsForm.handleSubmit((data: { payments: DocumentPaymentForm[] }) => {
                        const payments = data.payments.filter((payment) => !(payment.total === 0 && payment.kupa_account_id === null));
                        documentForm.setValue("payments", payments);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add documents closing to documentForm
            if (!settings?.display_items && !settings?.display_works && settings?.display_payments) {

                await new Promise<void>((resolve, reject) => {
                    documentClosingForm.handleSubmit((data: { documents: DocumentClosingForm[] }) => {
                        const documentsClosing = data.documents.filter((document) => document.status !== 'פתוח');
                        documentForm.setValue("documents_closing", documentsClosing);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add deposits to documentForm
            await new Promise<void>((resolve, reject) => {
                documentDepositsForm.handleSubmit((data: { deposits: DocumentDepositAdd[] }) => {
                    documentForm.setValue("deposits", data.deposits);
                    resolve();
                }, (errors) => {
                    reject(errors);
                    return;
                })();
            });


            // If All Forms have no errors, submit.
            await new Promise<void>((resolve, reject) => {
                documentForm.handleSubmit((data) => {
                    onSubmitDocument(data);
                    resolve();
                }, (errors) => {
                    reject(errors);
                    return;
                })();
            });


        } catch (error) {
            const appError = normalizeError(error);
            logError(appError, "documents-edit-submit-validation");
            toast.error(appError.userMessage);
        }

    };



    // *********************************************************************************************


    // submit the document after validation
    const onSubmitDocument: SubmitHandler<DocumentEdit> = async (documentData) => {

        setIsSaving(true);

        console.log("onSubmitDocument documentData:", documentData);

        try {
            const response = await saveDocumentService(documentData.id, documentData);
            console.log("document added: ", response);
            toast.success("המסמך נערך בהצלחה");
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        } finally {
            setIsSaving(false);
        }
    }






    // *********************************************************************************************


    // ------------------- end of add document functions -------------------------- //


    // ------------------- component jsx -------------------------- //


    // *********************************************************************************************



    return (
        <StyledDocument>

            { document && documentsSettings && settings && structure && documentsStructure && incomes && kupaAccounts ? <>


                {/************************************************  כותרת מסמך  ************************************************/}

                <Title
                    method={method}
                    documentForm={documentForm}
                    worksForm={worksForm}
                    itemsForm={itemsForm}
                    paymentsForm={paymentsForm}
                    settings={settings}
                    isDocumentClosed={isDocumentClosed}
                />

                {/************************************************  סוג מסמך, מספר, תאריך ושעה ************************************************/}

                <Section>
                    <Header
                        method={method}
                        documentForm={documentForm}
                        documentsSettings={documentsSettings}
                        settings={settings}
                        structure={structure}
                        isDocumentClosed={isDocumentClosed}
                    />
                </Section>
                


                {/************************************************  פרטי לקוח ורכב / חשבון  ************************************************/}

                <div className="details">

                    {/************************************************  פרטי לקוח  ************************************************/}

                    {settings?.display_customer && (
                        <Section>
                            <DocumentAccount
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                                isDocumentClosed={isDocumentClosed}
                            />
                        </Section>
                        
                    )}

                    {/************************************************  פרטי רכב  ************************************************/}

                    {settings?.display_car && (

                        <Section>
                            <DocumentCar
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                                isDocumentClosed={isDocumentClosed}
                            />
                        </Section>

                    )}

                    {/************************************************  פרטי בנק  ************************************************/}

                    {settings?.name === 'הפקדה' && (

                        <Section>
                            <DocumentBank
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                            />
                        </Section>
                        
                    )}
                </div>


                {/************************************************  עבודות  ************************************************/}

                {settings?.display_works && (

                    <Section>
                        <DocumentWorks
                            method={method}
                            documentForm={documentForm}
                            worksForm={worksForm}
                            paymentsForm={paymentsForm}
                            itemsForm={itemsForm}
                            worksFieldArray={worksFieldArray}
                            settings={settings}
                            structure={structure}
                            calcDocument={calcDocument}
                            isDocumentClosed={isDocumentClosed}
                        />
                    </Section>
                    
                )}


                {/************************************************  פריטים  ************************************************/}

                {settings?.display_items && (

                    <Section>
                        <DocumentItems
                            method={method}
                            documentForm={documentForm}
                            worksForm={worksForm}
                            paymentsForm={paymentsForm}
                            itemsForm={itemsForm}
                            itemsFieldArray={itemsFieldArray}
                            settings={settings}
                            structure={structure}
                            calcDocument={calcDocument}
                            isDocumentClosed={isDocumentClosed}
                        />
                    </Section>
                    
                )}

                {/************************************************  הצגת חשבוניות בקבלה  ************************************************/}

                {settings.name === "קבלה" && (

                    <Section>
                        <Invoices
                            method={method}
                            documentForm={documentForm}
                            paymentsForm={paymentsForm}
                            documentClosingForm={documentClosingForm}
                            documentsClosingFieldArray={documentsClosingFieldArray}
                            settings={settings}
                            setDocumentsToClose={setDocumentsToClose}
                            isDocumentClosed={isDocumentClosed}
                        />
                    </Section>
                    
                )}

                {/************************************************  בחירת אמצעי תשלום בקבלה  ************************************************/}

                {settings?.display_payments && kupaAccounts && (

                    <Section>
                        <Payments
                            method={method}
                            paymentsForm={paymentsForm}
                            paymentsFieldArray={paymentsFieldArray}
                            kupaAccounts={kupaAccounts}
                            isDocumentClosed={isDocumentClosed}
                            calcDocument={calcDocument}
                        />
                    </Section>
                    
                )}

                {/************************************************  הצגת שיקים בהפקדה  ************************************************/}

                {settings.name === "הפקדה" && (

                    <Section>
                        <Deposit
                            method={method}
                            documentForm={documentForm}
                            documentDepositsForm={documentDepositsForm}
                            depositsFieldArray={depositsFieldArray}
                            kupaAccounts={kupaAccounts}
                            isSelectDepositsDialogOpen={isSelectDepositsDialogOpen}
                            isSelectCashDialogOpen={isSelectCashDialogOpen}
                            setIsSelectDepositsDialogOpen={setIsSelectDepositsDialogOpen}
                            setIsSelectCashDialogOpen={setIsSelectCashDialogOpen}
                            isDocumentClosed={isDocumentClosed}
                        />
                    </Section>
                    
                )}



                {/************************************************  הצגת בועת סיכום  ************************************************/}

                <Section>
                    <Summary
                        method={method}
                        documentForm={documentForm}
                        settings={settings}
                        isDocumentClosed={isDocumentClosed}
                    />
                </Section>
                

                {/************************************************  כפתורים לשמירה או חזור  ************************************************/}

                <div className="buttons">
                    <Button onClick={saveDocument} variant='contained' color="primary" disabled={isSaving || isDocumentClosed}>שמירה</Button>
                    <Button variant='outlined' color="primary" onClick={()=>{navigate("/documents")}}>חזור</Button>
                </div>


                {/************************************************  הצגת טעינה  ************************************************/}

            </> : <Loading/>
            }

            {/* <DevTool control={itemsForm.control} /> */}


        </StyledDocument >
    )
}

export default EditDocument